import { FormField, Select } from '../ui/Form';
import { Search, Filter, Calendar, RefreshCw, BarChart3, FileText, User } from 'lucide-react';
import type { EnhancedJournalFilters } from '../../hooks/useEnhancedJournalFilters';

interface EnhancedJournalFiltersProps {
  filters: EnhancedJournalFilters;
  onFiltersChange: (filters: Partial<EnhancedJournalFilters>) => void;
  onReset: () => void;
  filterStats: {
    totalEntries: number;
    filteredCount: number;
    filteredPercentage: number;
    totalDebit: number;
    totalCredit: number;
    isBalanced: boolean;
  };
  journalTypeOptions: Array<{ value: string; label: string }>;
  statusOptions: Array<{ value: string; label: string }>;
  sourceOptions: Array<{ value: string; label: string }>;
  isFiltersActive: boolean;
}

export function EnhancedJournalFilters({ 
  filters, 
  onFiltersChange, 
  onReset, 
  filterStats,
  journalTypeOptions,
  statusOptions,
  sourceOptions,
  isFiltersActive
}: EnhancedJournalFiltersProps) {
  const handleFilterChange = (field: keyof EnhancedJournalFilters, value: string) => {
    onFiltersChange({
      [field]: value
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
      {/* Header avec statistiques */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Filtres des écritures</h3>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                {filterStats.filteredCount} / {filterStats.totalEntries} écritures
                {filterStats.filteredPercentage < 100 && (
                  <span className="text-blue-600 dark:text-blue-400">({filterStats.filteredPercentage}%)</span>
                )}
              </span>
              <span className={`flex items-center gap-1 ${filterStats.isBalanced ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {filterStats.isBalanced ? '✓' : '⚠'} 
                {filterStats.isBalanced ? 'Équilibré' : 'Déséquilibré'}
              </span>
            </div>
          </div>
        </div>
        {isFiltersActive && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Réinitialiser
          </button>
        )}
      </div>

      {/* Grille de filtres */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
        {/* Recherche - 2 colonnes sur xl */}
        <div className="xl:col-span-2">
          <FormField label="Recherche globale">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Référence, description, compte..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
              {filters.search && (
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ×
                </button>
              )}
            </div>
          </FormField>
        </div>

        {/* Type de journal */}
        <FormField label="Type de journal">
          <div className="relative">
            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 z-10" />
            <Select
              value={filters.journalType}
              onChange={(e) => handleFilterChange('journalType', e.target.value)}
              options={journalTypeOptions}
              className="pl-10"
            />
          </div>
        </FormField>

        {/* Statut */}
        <FormField label="Statut">
          <Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            options={statusOptions}
          />
        </FormField>

        {/* Source */}
        <FormField label="Source">
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
            <Select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              options={sourceOptions}
              className="pl-10"
            />
          </div>
        </FormField>

        {/* Date début */}
        <FormField label="Date début">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {filters.startDate && (
              <button
                onClick={() => handleFilterChange('startDate', '')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            )}
          </div>
        </FormField>

        {/* Date fin */}
        <FormField label="Date fin">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {filters.endDate && (
              <button
                onClick={() => handleFilterChange('endDate', '')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            )}
          </div>
        </FormField>
      </div>

      {/* Résumé des filtres actifs */}
      {isFiltersActive && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                <Search className="h-3 w-3" />
                "{filters.search}"
                <button 
                  onClick={() => handleFilterChange('search', '')}
                  className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.journalType && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded-full">
                <FileText className="h-3 w-3" />
                {journalTypeOptions.find(opt => opt.value === filters.journalType)?.label}
                <button 
                  onClick={() => handleFilterChange('journalType', '')}
                  className="hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs rounded-full">
                {statusOptions.find(opt => opt.value === filters.status)?.label}
                <button 
                  onClick={() => handleFilterChange('status', '')}
                  className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.source && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs rounded-full">
                <User className="h-3 w-3" />
                {sourceOptions.find(opt => opt.value === filters.source)?.label}
                <button 
                  onClick={() => handleFilterChange('source', '')}
                  className="hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {(filters.startDate || filters.endDate) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs rounded-full">
                <Calendar className="h-3 w-3" />
                {filters.startDate && filters.endDate 
                  ? `${filters.startDate} - ${filters.endDate}`
                  : filters.startDate 
                    ? `Depuis ${filters.startDate}`
                    : `Jusqu'au ${filters.endDate}`
                }
                <button 
                  onClick={() => {
                    handleFilterChange('startDate', '');
                    handleFilterChange('endDate', '');
                  }}
                  className="hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
